import React, { useEffect, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Package,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload, { imageSrc } from "@/components/ImageUpload";

const blank = {
  product_code: "",
  name: "",
  category_id: "",
  metal_id: "",
  purity_id: "",
  huid: "",
  barcode: "",
  rfid: "",
  gross_weight: 0,
  stone_weight: 0,
  net_weight: 0,
  wastage_percent: 0,
  making_charge: 0,
  stone_charge: 0,
  sale_price: 0,
  image_url: "",
  branch_id: "",
  quantity: 1,
  design: "",
};

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [refs, setRefs] = useState({
    categories: [],
    metals: [],
    purities: [],
    branches: [],
  });

  const loadRefs = async () => {
    const [c, m, p, b] = await Promise.all([
      api.get("/categories"),
      api.get("/metals"),
      api.get("/purities"),
      api.get("/branches"),
    ]);
    setRefs({
      categories: c.data,
      metals: m.data,
      purities: p.data,
      branches: b.data,
    });
  };
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/products${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      );
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadRefs();
    load();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...blank, branch_id: refs.branches[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...blank, ...p });
    setOpen(true);
  };

  const save = async () => {
    if (!form.product_code.trim() || !form.name.trim()) {
      toast.error("Product code & name required");
      return;
    }
    if (saving) return;
    setSaving(true);
    const payload = { ...form };
    [
      "gross_weight",
      "stone_weight",
      "net_weight",
      "wastage_percent",
      "making_charge",
      "stone_charge",
      "sale_price",
      "quantity",
    ].forEach((k) => {
      payload[k] = Number(form[k]) || 0;
    });
    payload.net_weight =
      payload.net_weight ||
      Math.max(payload.gross_weight - payload.stone_weight, 0);
    // null-out empty optional ids
    [
      "category_id",
      "metal_id",
      "purity_id",
      "branch_id",
      "huid",
      "barcode",
      "rfid",
      "image_url",
      "design",
    ].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Updated" : "Added to inventory");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const lookup = (arr, id) => arr.find((x) => x.id === id);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="inventory-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Stock
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Inventory
          </h1>
        </div>
        <Button
          data-testid="add-product-btn"
          onClick={openCreate}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> Add product
        </Button>
      </header>

      <div className="relative max-w-md">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          data-testid="product-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by code, name, barcode, HUID..."
          className="pl-9 bg-card border-border"
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Product
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Category
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Metal/Purity
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Net Wt.
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Qty
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Price
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-10 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-10 text-muted-foreground"
                >
                  No products yet. Add your first SKU.
                </TableCell>
              </TableRow>
            ) : (
              products.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-md bg-secondary border border-border grid place-items-center overflow-hidden">
                        {p.image_url ? (
                          <img
                            src={imageSrc(p.image_url)}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Package
                            size={16}
                            className="text-muted-foreground"
                          />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {p.product_code}
                          {p.huid ? ` · HUID ${p.huid}` : ""}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {lookup(refs.categories, p.category_id)?.name || "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {lookup(refs.metals, p.metal_id)?.name || "—"}
                    <span className="text-muted-foreground">
                      {" "}
                      · {lookup(refs.purities, p.purity_id)?.name || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {grams(p.net_weight)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.quantity}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(p.sale_price)}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => openEdit(p)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                    >
                      <Trash size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["product_code", "Product Code *"],
              ["name", "Name *"],
              ["huid", "HUID"],
              ["barcode", "Barcode"],
              ["rfid", "RFID"],
              ["design", "Design"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {l}
                </Label>
                <Input
                  data-testid={`field-product-${k}`}
                  className="mt-1.5 bg-background border-border"
                  value={form[k] ?? ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}

            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Category
              </Label>
              <Select
                value={form.category_id || ""}
                onValueChange={(v) => setForm({ ...form, category_id: v })}
              >
                <SelectTrigger
                  data-testid="field-product-category"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {refs.categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Metal
              </Label>
              <Select
                value={form.metal_id || ""}
                onValueChange={(v) => setForm({ ...form, metal_id: v })}
              >
                <SelectTrigger
                  data-testid="field-product-metal"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {refs.metals.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Purity
              </Label>
              <Select
                value={form.purity_id || ""}
                onValueChange={(v) => setForm({ ...form, purity_id: v })}
              >
                <SelectTrigger
                  data-testid="field-product-purity"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {refs.purities.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {[
              ["gross_weight", "Gross Wt (g)"],
              ["stone_weight", "Stone Wt (g)"],
              ["net_weight", "Net Wt (g)"],
              ["wastage_percent", "Wastage %"],
              ["making_charge", "Making (₹)"],
              ["stone_charge", "Stone Chg (₹)"],
              ["sale_price", "Sale Price (₹)"],
              ["quantity", "Quantity"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {l}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid={`field-product-${k}`}
                  className="mt-1.5 bg-background border-border tabular-nums"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}

            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUpload
                testid="field-product-image"
                label="Product photo"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
              />
              <ImageUpload
                testid="field-product-hallmark"
                label="Hallmark certificate"
                value={form.hallmark_cert_url}
                onChange={(url) => setForm({ ...form, hallmark_cert_url: url })}
                accept="image/*,application/pdf"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-product-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {editing ? "Save changes" : "Add product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
